import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Checkbox,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
} from "@material-ui/core";
import { useFormik } from "formik";
import { toast } from "react-toastify";

import {
  RE_ASSIGN_DRIVER,
  RE_ASSIGN_DRIVER_SUCCESS,
  RE_ASSIGN_DRIVER_FAILURE,
  RE_ASSIGN_CHANGE_DRIVER,
  RE_ASSIGN_CHANGE_DRIVER_SUCCESS,
  RE_ASSIGN_CHANGE_DRIVER_FAILURE,
  CHANGE_DRIVER,
  CHANGE_DRIVER_SUCCESS,
  CHANGE_DRIVER_FAILURE,
  CHECK_RELATED_PRIME_JOB,
  CHECK_RELATED_PRIME_JOB_SUCCESS,
  CHECK_RELATED_PRIME_JOB_FAILURE,
  RE_ASSIGN_CONSOLIDATED_JOB,
  RE_ASSIGN_CONSOLIDATED_JOB_SUCCESS,
  RE_ASSIGN_CONSOLIDATED_JOB_FAILURE,
  FETCH_LOCAL_DRIVERS,
  FETCH_LOCAL_DRIVERS_SUCCESS,
  FETCH_LOCAL_DRIVERS_FAILURE
} from "@utils/actionTypes";
import API from "@services/axios";
import { useStore } from "@store/store";
import Loader from "@components/loader";
import Pagination from "@components/pagination";
import checkedIconOrange from "@assets/images/cheked-icon-orange.svg";
import uncheckedIcon from "@assets/images/uncheck-icon.svg";
import { schema } from "@utils/schemas";
import validationSchema from "@utils/validationSchemas";
import {
  dashboardRequestStatusColors,
  jobListingRequestStyle,
  utcToTimezone,
  getPermissions,
  isRolePermission,
} from "@utils/commonFunctions";
import { dashboardListingHeader, routes, jobStatus } from "@utils/constant";

import { TableStyle } from "./style";
import Row from "./row";
import AssignDriverPopup from "../assignDriverPopup";

function CargoWiseJobTableListing(props) {
  const classes = TableStyle();
  const [state, dispatch] = useStore();
  const [openDriverPopup, setOpenDriverPopup] = useState(false);
  const [error, setError] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [jobId, setJobId] = useState("");
  const [truckId, setTruckId] = useState("");
  const [cityId, setCityId] = useState("");
  const [currentDriverId, setCurrentDriverId] = useState("");

  const isPermission =
    getPermissions() && isRolePermission(getPermissions(), "jobReview");
  const isCompleted = state.job.cargoWiseJobData?.rows?.map((item) => {
    return item.jobStatuses && item.jobStatuses.name;
  });

  useEffect(() => {
    if (openDriverPopup) {
      getDriver();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityId, openDriverPopup]);

  const getDriver = () => {
    dispatch({ type: FETCH_LOCAL_DRIVERS });
    API.get("drivers/local", {
      params: {
        cityId: cityId ? cityId : undefined,
      },
    })
      .then((response) => {
        const data = response.data.data.filter(d => d.id !== currentDriverId);
        dispatch({
          type: FETCH_LOCAL_DRIVERS_SUCCESS,
          payload: data,
        });
      })
      .catch((error) => {
        dispatch({ type: FETCH_LOCAL_DRIVERS_FAILURE, payload: error });
      });
  };

  const handleDriverPopupOpen = (type, id, status, truck, city, driverId) => {
    setJobId(id);
    setOpenDriverPopup(true);
    setStatus(status);
    setTruckId(truck);
    setType(type);
    setCityId(city);
    setCurrentDriverId(driverId);
  };

  const handleAssignDriverPopupClose = () => {
    setOpenDriverPopup(false);
    assignDriverFormik.handleReset();
    setError("");
    setJobId("");
    setStatus("");
    setTruckId("");
    setType("");
    setCityId("");
    setCurrentDriverId("");
  };

  // API calling to assign and reassign driver depending on status
  const assignDriverFormik = useFormik({
    initialValues: schema.assignDriverSchema,
    validationSchema: validationSchema.assignDriverValidationSchema,
    onSubmit: async (value) => {
      if (["Rejected", "Assigned"].includes(status)) {
        const relatedPrimeJobId = await checkRelatedPrimeJob(jobId);
        if (relatedPrimeJobId) {
          reassignConsolidatedJobToDriver(value.assignDriver, relatedPrimeJobId);
        } else {
          reassignLocalJobToDriver(value.assignDriver, jobId);
        }
      } else {
        if (
          ["In Transit", "Lodgement", "Loading", "Unloading"].includes(status)
        ) {
          dispatch({ type: CHANGE_DRIVER });
          API.put(`jobs/${jobId}/modifyDriver`, {
            newDriverId: value.assignDriver,
          })
            .then((response) => {
              dispatch({
                type: CHANGE_DRIVER_SUCCESS,
                payload: response.data.data,
              });
              handleAssignDriverPopupClose();
              toast.success("Driver Re-Assigned Successfully");
              props.getCargoWiseJob();
            })
            .catch((error) => {
              dispatch({
                type: CHANGE_DRIVER_FAILURE,
                payload: error,
              });
              setError(error.response.data.message);
            });
        } else {
          dispatch({ type: RE_ASSIGN_CHANGE_DRIVER });
          API.put(`jobs/${jobId}/changeDriver`, {
            driverId: value.assignDriver,
            truckId: truckId,
          })
            .then((response) => {
              dispatch({
                type: RE_ASSIGN_CHANGE_DRIVER_SUCCESS,
                payload: response.data.data,
              });
              handleAssignDriverPopupClose();
              toast.success("Driver Re-Assigned Successfully");
              props.getCargoWiseJob();
            })
            .catch((error) => {
              dispatch({
                type: RE_ASSIGN_CHANGE_DRIVER_FAILURE,
                payload: error,
              });
              setError(error.response.data.message);
            });
        }
      }
    }
  });

  const reassignLocalJobToDriver = (driverId, jobId) => {
    dispatch({ type: RE_ASSIGN_DRIVER });
    API.put(`jobs/${jobId}/reassignJob`, { driverId })
      .then((response) => {
        dispatch({
          type: RE_ASSIGN_DRIVER_SUCCESS,
          payload: response.data.data,
        });
        handleAssignDriverPopupClose();
        toast.success("Driver Re-Assigned Successfully");
        props.getCargoWiseJob();
      })
      .catch((error) => {
        dispatch({ type: RE_ASSIGN_DRIVER_FAILURE, payload: error });
        setError(error.response.data.message);
      });
  }

  const reassignConsolidatedJobToDriver = (driverId, primeJobId) => {
    dispatch({ type: RE_ASSIGN_CONSOLIDATED_JOB });
    API.put(`jobs/${primeJobId}/reassignConsolidatedJob`, { driverId })
      .then((response) => {
        dispatch({
          type: RE_ASSIGN_CONSOLIDATED_JOB_SUCCESS,
          payload: response.data.data,
        });
        handleAssignDriverPopupClose();
        toast.success(`Jobs ${response.data.data.join(", ")} in the prime job P‑${primeJobId} have been reassigned successfully`);
        props.getCargoWiseJob();
      })
      .catch((error) => {
        dispatch({ type: RE_ASSIGN_CONSOLIDATED_JOB_FAILURE, payload: error });
        toast.error(`One or more jobs in prime group P‑${primeJobId} cannot be reassigned. Please check job statuses and try again.`);
        setError(error.response.data.message);
      });
  }

  // API calling to check related prime job before assign driver
  const checkRelatedPrimeJob = (jobId) => {
    dispatch({ type: CHECK_RELATED_PRIME_JOB });
    return API.get(`jobs/${jobId}/checkRelatedPrimeJob`)
      .then((response) => {
        dispatch({
          type: CHECK_RELATED_PRIME_JOB_SUCCESS,
        });
        return response.data.data.id;
      })
      .catch((error) => {
        dispatch({
          type: CHECK_RELATED_PRIME_JOB_FAILURE,
        });
        return null;
      });
  }

  return (
    <>
      <div className={classes.TableWrapper}>
        <Loader loading={
          state.job?.loadingCargoWiseJob ||
          state.job?.reviewingJobs
        } />
        <TableContainer
          component={Paper}
          className={classes.customJobTable}
        >
          <Table stickyHeader aria-label="simple table">
            <TableHead>
              <TableRow>
                {isPermission &&
                  isCompleted &&
                  isCompleted.includes("Completed") ? (
                  <TableCell className="check">
                    <FormControlLabel
                      className="custom-checkbox"
                      control={
                        <Checkbox
                          icon={<img src={uncheckedIcon} alt="CheckBox" />}
                          checkedIcon={
                            <img src={checkedIconOrange} alt="CheckBox" />
                          }
                          onChange={(e) => props.handleMainChange(e)}
                          checked={props._maincheck}
                          name="check"
                        />
                      }
                    />
                  </TableCell>
                ) : (
                  <TableCell className="check"></TableCell>
                )}

                {dashboardListingHeader.filter(item => item.className !== "net-charge").map((item, index) => {
                  return (
                    <TableCell key={index} className={item.className}>
                      <div className="flex-box">
                        <span className="title-cell">{item.title}</span>
                        {item.sort && (
                          <TableSortLabel
                            direction={
                              props.orderBy === item.sortTitle
                                ? props.order
                                : "desc"
                            }
                            active={true}
                            onClick={(e) => props.handleSorting(e, item)}
                          />
                        )}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {state.job?.cargoWiseJobData?.count === 0 ? (
                <TableRow className="no-data">
                  <TableCell colSpan={12}>
                    <span>No Data Found</span>
                  </TableCell>
                </TableRow>
              ) : (
                props?.dataTable?.map((row, index) => {

                  return row?.primeConsolidatedJobs?.length > 0 ? (
                    <Row
                      key={index}
                      row={row}
                      index={index}
                      uncheckedIcon={uncheckedIcon}
                      checkedIconOrange={checkedIconOrange}
                      handleChange={props.handleChange}
                      dashboardRequestStatusColors={dashboardRequestStatusColors}
                      handleDriverPopupOpen={handleDriverPopupOpen}
                      isPermission={isPermission}
                    />
                  ) : (
                    <TableRow key={index}>
                      {isPermission &&
                        row.jobStatuses &&
                        row.jobStatuses.name === "Completed" ? (
                        <TableCell className="check">
                          <FormControlLabel
                            className="custom-checkbox"
                            control={
                              <Checkbox
                                icon={
                                  <img src={uncheckedIcon} alt="CheckBox" />
                                }
                                checkedIcon={
                                  <img src={checkedIconOrange} alt="CheckBox" />
                                }
                                onChange={(e) => props.handleChange(row, e)}
                                checked={
                                  row["_rowChecked"] === true
                                }
                                name="check"
                              />
                            }
                          />
                        </TableCell>
                      ) : (
                        <TableCell></TableCell>
                      )}
                      <TableCell className="jobId">
                        <Link
                          to={`${routes.jobDetail}/${row.id}`}
                          target="_blank"
                        >
                          {row.id ? `${row.id}` : "-"}
                        </Link>
                      </TableCell>
                      <TableCell className="city">{row?.cities?.name || "-"}</TableCell>
                      <TableCell
                        className={`driver ${jobListingRequestStyle(
                          row.jobStatuses &&
                            row.jobStatuses?.name === jobStatus.NOT_ASSIGNED
                            ? "Assign Driver"
                            : row.jobStatuses?.name === jobStatus.REJECTED ||
                              row.jobStatuses?.name === jobStatus.CHANGE_DRIVER
                              ? "Re-Assign"
                              : ""
                        )}`}
                      >
                        {row.drivers?.name ? (
                          row.jobStatuses?.name === jobStatus.CHANGE_DRIVER ? (
                            <span
                              className="re-assign-link"
                              onClick={() =>
                                handleDriverPopupOpen(
                                  "ReAssign",
                                  row.id,
                                  row.jobStatuses?.name,
                                  row.trucks?.id,
                                  row.cities.id,
                                  row.drivers?.id,
                                )
                              }
                            >
                              {row.drivers?.name}
                            </span>
                          ) : [
                            jobStatus.ASSIGNED,
                            jobStatus.IN_TRANSIT,
                            jobStatus.LODGEMENT,
                            jobStatus.LOADING,
                            jobStatus.UNLOADING,
                          ].includes(row.jobStatuses?.name) ? (
                            <span
                              className="re-assign-link"
                              onClick={() =>
                                handleDriverPopupOpen(
                                  "ReAssign",
                                  row.id,
                                  row.jobStatuses?.name,
                                  row.trucks?.id,
                                  row.cities.id,
                                  row.drivers?.id,
                                )
                              }
                            >
                              {row.drivers?.name}
                            </span>
                          ) : (
                            row.drivers?.name
                          )
                        ) : (
                          <Button
                            color="primary"
                            className="primary-btn"
                            onClick={() =>
                              handleDriverPopupOpen(
                                "ReAssign",
                                row.id,
                                row.jobStatuses?.name,
                                row.trucks?.id,
                                row.cities.id,
                                row.drivers?.id,
                              )
                            }
                          >
                            Re-Assign driver
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="truck-rego">
                        {row.trucks ? row.trucks.rego : "-"}
                      </TableCell>
                      <TableCell
                        className={`status ${dashboardRequestStatusColors(
                          row.jobStatuses ? row.jobStatuses.name : ""
                        )}`}
                      >
                        <span>
                          {row.jobStatuses ? row.jobStatuses.name : "-"}
                        </span>
                      </TableCell>
                      <TableCell className="customerName">
                        {row.customers ? row.customers.name : "-"}
                      </TableCell>
                      <TableCell className="cto">
                        {row.ctos ? row.ctos.name : "-"}
                      </TableCell>
                      <TableCell className="jobType">
                        {row.jobTypes ? row.jobTypes.name : "-"}
                      </TableCell>
                      <TableCell className="cargoType">
                        {row.cargoTypes ? row.cargoTypes.name : "-"}
                      </TableCell>
                      <TableCell className="date">
                        {row.createdAt
                          ? utcToTimezone(
                            row.createdAt,
                            row.cities?.timezone,
                            "DD/MM/YYYY",
                          )
                          : "-"}
                      </TableCell>
                      <TableCell className="completion-date">
                        {row?.completedAt
                          ? utcToTimezone(
                            row?.completedAt,
                            row?.cities?.timezone,
                            "DD/MM/YYYY"
                          )
                          : "-"}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {state.job?.cargoWiseJobData?.count !== 0 && (
          <div className="pagination-wrapper">
            <Pagination
              count={state.job?.cargoWiseJobData?.count}
              page={props.page}
              handleChangePage={props.handleChangePage}
              rowsPerPage={props.rowsPerPage}
              handleChangeRowsPerPage={props.handleChangeRowsPerPage}
            />
          </div>
        )}
      </div>
      <AssignDriverPopup
        handleClose={handleAssignDriverPopupClose}
        open={openDriverPopup}
        formik={assignDriverFormik}
        error={error}
        type={type}
      />
    </>
  );
}

export default CargoWiseJobTableListing;
